<template>
  <div>
    <v-overlay absolute v-if="loading">
      <v-progress-circular
          indeterminate
          :size="50"
          :width="5"
          color="primary"
      >
      </v-progress-circular>
    </v-overlay>

    <v-card class="pa-0 ma-4">
      <v-card-title>{{ title }}</v-card-title>
      <v-card-text>
        <v-row>
          <v-col>
            <v-select
              v-bind:label="$t('Group')"
              v-model="filter.group"
              :items="groupList"
              item-text="text"
              item-value="id"
              @change="updateGrid"
              clearable
            ></v-select>
          </v-col>
          <v-col>
            <DatePickerISOCompliant
              v-model="filter.from"
              :label="$t('From')"
              clearable
              @change="updateGrid"
            />
          </v-col>
          <v-col>
            <DatePickerISOCompliant
              v-model="filter.to"
              :label="$t('To')"
              clearable
              @change="updateGrid"
            />
          </v-col>
          <v-col>
            <v-autocomplete
              v-bind:label="$t('User')"
              v-model="filter.userIds"
              :items="userList"
              item-text="text"
              item-value="id"
              multiple
              @change="updateGrid"
              clearable
            />
          </v-col>
          <v-col>
            <v-autocomplete
              v-bind:label="$t('Type')"
              v-model="filter.typeIds"
              :items="typeList"
              item-text="text"
              item-value="id"
              multiple
              @change="updateGrid"
              clearable
            />
          </v-col>
          <v-col>
            <v-autocomplete
              v-bind:label="$t('Source')"
              v-model="filter.sourceIds"
              :items="sourceList"
              item-text="text"
              item-value="id"
              multiple
              @change="updateGrid"
              clearable
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <v-card class="pa-0 ma-4 mt-0">
      <v-card-text class="pa-0 mt-0">
        <simple-table
              ref="auditTableInstanceKey"
              :tableConfig="auditTableConfig"
              :fetchData="getData"
              :tableData="tableData"
        ></simple-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import AuditService from '@/services/AuditService';
import UserService from '@/services/UserService';
import AuditType from '@/ecm/variables/AuditType.js';
import Utils from '@/ecm/common/Utils';
import SimpleTable from '@/components/base/SimpleTable.vue';
import DatePickerISOCompliant from '@/components/base/DatePickerISOCompliant.vue';

export default {
    components: {
        SimpleTable,
        DatePickerISOCompliant
    },
    data() {
        return {
            title: this.$t('Audits'),
            groupList: [
                { id: '1', text: this.$t('Administration') },
                { id: '2', text: this.$t('Documents') },
                { id: '3', text: this.$t('Searches') },
                { id: '4', text: this.$t('Flows') },
                { id: '5', text: this.$t('Delegations') },
            ],
            userList: [],
            typeList: [],
            sourceList: [],
            auditTableInstanceKey: 'auditTableInstanceKey',
            filter: {
                group: null,
                from: null,
                to: null,
                userIds: null,
                typeIds: null,
                sourceIds: null,
            },
            dataSource: [],
            loading: false,
            tableData: [],
        };
    },
    created() {
        AuditService.getTypes().then((data) => {
            this.typeList = data;
        });
        AuditService.getSources().then((data) => {
            this.sourceList = data;
        });
        UserService.getList().then((data) => {
            this.userList = data;
        });
    },
    methods: {
        async getData(pagination) {
            this.loading = true;

            pagination.dateFrom = this.filter.from;
            pagination.dateTo = this.filter.to;
            pagination.group = parseInt(this.filter.group);
            pagination.userIDs = this.filter.userIds || [];
            pagination.auditTypeIDs = this.filter.typeIds || [];
            pagination.auditSourceIDs = this.filter.sourceIds || [];
            if (pagination.orderBy === '') {
                pagination.orderBy = 'Date';
                pagination.desc = true;
            }

            const data = await AuditService.getGrid(pagination);
            this.loading = false;
            this.dataSource = data.data;
            this.tableData = this.transformTableData(data.data);
            return {
                totalItems: data.totalCount,
                totalPages: Math.ceil(data.totalCount / pagination.pageSize),
                result: data.data
            };
        },
        transformTableData(rows) {
            return rows.map(row => {
                const tableFields = [
                    { title: row.Date, function: () => this.calculateCellValue(row.Date), },
                    { title: row.Type, },
                    { title: row.User, },
                    { title: row.Description, },
                ];

                return {
                    id: row.Id,
                    fields: tableFields,
                };
            });
        },
        updateGrid() {
            this.filterTypesByGroups();
            this.auditTableInstance.resetPagination();
        },
        filterTypesByGroups() {
            AuditService.getTypes().then((data) => {
                this.typeList = data;
                const typesInGroup = this.getTypesOfGroup();

                if (typesInGroup.length !== 0) {
                    const newTypes = [];
                    for (let l = 0; l < this.typeList.length; l++) {
                        const id = parseInt(this.typeList[l].id);
                        if (typesInGroup.includes(id)) {
                            newTypes.push(this.typeList[l]);
                        }
                    }
                    this.typeList = newTypes;
                }
            });
        },
        getTypesOfGroup() {
            switch (this.filter.group) {
            case this.groupList[0].id:
                return [
                    AuditType.ADMINISTRATION_DELETE,
                    AuditType.ADMINISTRATION_UPDATE,
                    AuditType.ADMINISTRATION_CREATE,
                ];
            case this.groupList[1].id:
                return [
                    AuditType.DOCUMENT_DELETE,
                    AuditType.DOCUMENT_INSERT,
                    AuditType.DOCUMENT_UPDATE,
                    AuditType.DOCUMENT_UPDATE_ATTACHMENT,
                    AuditType.DOCUMENT_DELETE_PERMANENTLY,
                    AuditType.DOCUMENT_RESTORE,
                    AuditType.DOCUMENT_ARCHIVE,
                    AuditType.DOCUMENT_DOWNLOAD,
                    AuditType.DOCUMENT_DISPLAY,
                    AuditType.DOCUMENT_PRINT,
                    AuditType.DOCUMENT_AUTO_UPDATE,
                    AuditType.DOCUMENT_CONTENT_TYPE_CHANGE,
                    AuditType.DOCUMENT_SIGN,
                    AuditType.DOCUMENT_SEND_BY_EMAIL,
                    AuditType.COMMENT_CREATE,
                    AuditType.COMMENT_CREATE_EXTERNAL,
                    AuditType.COMMENT_UPDATE,
                    AuditType.COMMENT_DELETE,
                    AuditType.DOCUMENT_ATTACHMENT_INSERT,
                    AuditType.DOCUMENT_ATTACHMENT_UPDATE,
                    AuditType.DOCUMENT_ATTACHMENT_DELETE,
                    AuditType.DOCUMENT_ATTACHMENT_DISPLAY,
                    AuditType.DOCUMENT_ATTACHMENT_DOWNLOAD,
                    AuditType.DOCUMENT_DISPLAY_EXTERNAL,
                    AuditType.DOCUMENT_SIGN_EXTERNAL,
                    AuditType.DOCUMENT_DOWNLOAD_EXTERNAL,
                    AuditType.DOCUMENT_VALIDATE_EXTERNAL,
                    AuditType.DOCUMENT_REJECT_EXTERNAL,
                ];
            case this.groupList[2].id:
                return [
                    AuditType.USER_ADVANCED_SEARCH,
                    AuditType.USER_NATURAL_SEARCH,
                    AuditType.USER_BASKET_SEARCH,
                    AuditType.USER_NOTIFICATION_SEARCH,
                ];
            case this.groupList[3].id:
                return [
                    AuditType.FLOW_VALIDATE_ACTION,
                    AuditType.FLOW_REFUSE_ACTION,
                    AuditType.FLOW_DISTRIBUTE_ACTION,
                ];
            case this.groupList[4].id:
                return [
                    AuditType.DELEGATION_SCHEDULED_START,
                    AuditType.DELEGATION_SCHEDULED_END,
                    AuditType.DELEGATION_MANUAL_START,
                    AuditType.DELEGATION_MANUAL_END,
                ];
            default:
                return [];
            }
        },
        calculateCellValue(delDate) {
            return Utils.date.toUserFormatFromIso(delDate);
        }
    },
    computed: {
        auditTableInstance() {
            return this.$refs[this.auditTableInstanceKey];
        },
        auditTableConfig() {
            const tableHeaders = [];
            tableHeaders.push({ title: 'Date', titleTranslation: 'Date', allowSorting: true });
            tableHeaders.push({ title: 'Type', titleTranslation: 'Type' });
            tableHeaders.push({ title: 'User', titleTranslation: 'User' });
            tableHeaders.push({ title: 'Description', titleTranslation: 'Description' });

            return {
                dense: true,
                allowSelection: false,
                showActions: false,
                showPagination: true,
                headers: tableHeaders,
                defaultPageSize: 25,
                pageSizes: [25, 50, 100, 500],
            };
        }
    },
};
</script>
